/**
 * Push Subscription Manager (Spec §6.5, §9)
 *
 * Handles Web Push registration, VAPID key fetch, permission flow,
 * and startup reconciliation.
 *
 * Spec §6.5: POST /subscribe requires per-blob workspace access tokens
 * with subscribe=true. The v2 format uses subscriptions[] entries instead
 * of raw blobIds.
 */

import { recordBrowserHttpErrorResponse } from '@secureblob/sdk/browserHttpErrors'

/** Auth provider interface — mirrors SDK WorkspaceAuthProvider */
export interface PushAuthProvider {
    getAccessToken(scope: 'read' | 'write'): Promise<{ token: string; expiresAt: string }>
}

/** Per-blob subscription entry with auth provider for token acquisition */
export interface PushBlobSubscription {
    blobId: string
    authProvider: PushAuthProvider
}

export interface NotificationDeviceRecord {
    subscriptionId: string
    createdAt: string
    lastSeenAt: string
    deviceLabel?: string
    platform?: string
}

export interface PushSubscriptionManager {
    subscribe(apiBaseUrl: string, subscriptions: PushBlobSubscription[]): Promise<boolean>
    unsubscribe(apiBaseUrl: string, subscriptions?: PushBlobSubscription[]): Promise<boolean>
    reconcile(apiBaseUrl: string, subscriptions: PushBlobSubscription[]): Promise<void>
    isSubscribed(): Promise<boolean>
}

function getPushApiBase(apiBaseUrl: string): string {
    return apiBaseUrl.replace(/\/api\/cryptoblobstore$/, '')
}

/** Check if app is running as installed PWA */
export function isPwaInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches
        || (navigator as unknown as { standalone?: boolean }).standalone === true
}

async function getCurrentBrowserPushSubscription(): Promise<PushSubscription | null> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null
    const registration = await navigator.serviceWorker.ready
    return registration.pushManager.getSubscription()
}

function getTestModePushSubscriptionIdOverride(): string | null {
    if (typeof window === 'undefined') return null
    const override = (window as unknown as {
        __sb_testContext?: { currentPushSubscriptionIdOverride?: string | null }
    }).__sb_testContext?.currentPushSubscriptionIdOverride
    return typeof override === 'string' && override.length > 0 ? override : null
}

/** Fetch VAPID public key from server (cached per apiBaseUrl). */
const vapidKeyCache = new Map<string, string | null>()
async function getVapidKey(apiBaseUrl: string): Promise<string | null> {
    const pushApiBase = getPushApiBase(apiBaseUrl)
    if (vapidKeyCache.has(pushApiBase)) return vapidKeyCache.get(pushApiBase) ?? null
    try {
        const vapidKeyUrl = `${pushApiBase}/api/push/vapid-key`
        const res = await fetch(vapidKeyUrl)
        recordBrowserHttpErrorResponse(res, { url: vapidKeyUrl, method: 'GET' })
        if (!res.ok) return null
        const data = await res.json() as { publicKey?: string }
        const key = data.publicKey ?? null
        vapidKeyCache.set(pushApiBase, key)
        return key
    } catch {
        return null
    }
}

/** Clear the VAPID key cache (test-only). */
export function clearVapidKeyCache(): void {
    vapidKeyCache.clear()
}

/** Convert VAPID base64url key to Uint8Array for applicationServerKey */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
}

function encodeBase64Url(bytes: Uint8Array): string {
    let binary = ''
    for (const byte of bytes) {
        binary += String.fromCharCode(byte)
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function detectNotificationPlatform(): string | undefined {
    const platformCandidates = [
        (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform,
        navigator.platform,
        navigator.userAgent,
    ].filter((value): value is string => typeof value === 'string' && value.length > 0)

    const raw = platformCandidates.join(' ').toLowerCase()
    if (!raw) return undefined
    if (raw.includes('iphone') || raw.includes('ipad') || raw.includes('ios')) return raw.includes('ipad') ? 'ipados' : 'ios'
    if (raw.includes('android')) return 'android'
    if (raw.includes('cros')) return 'chromeos'
    if (raw.includes('mac')) return 'macos'
    if (raw.includes('win')) return 'windows'
    if (raw.includes('linux') || raw.includes('x11')) return 'linux'
    return undefined
}

function detectNotificationBrowser(): string | undefined {
    const userAgent = navigator.userAgent.toLowerCase()
    const brands = ((navigator as Navigator & { userAgentData?: { brands?: Array<{ brand: string }> } }).userAgentData?.brands ?? [])
        .map((brand) => brand.brand.toLowerCase())

    if (brands.some((brand) => brand.includes('edge')) || userAgent.includes('edg/')) return 'Edge'
    if (brands.some((brand) => brand.includes('chrome')) || userAgent.includes('chrome/') || userAgent.includes('crios/')) return 'Chrome'
    if (brands.some((brand) => brand.includes('firefox')) || userAgent.includes('firefox/')) return 'Firefox'
    if (userAgent.includes('safari/') && !userAgent.includes('chrome/') && !userAgent.includes('crios/') && !userAgent.includes('edg/')) return 'Safari'
    return undefined
}

function getNotificationDeviceMetadata(): { deviceLabel?: string; platform?: string } {
    const platform = detectNotificationPlatform()
    const browser = detectNotificationBrowser()
    const platformLabels: Record<string, string> = {
        android: 'Android',
        chromeos: 'ChromeOS',
        ios: 'iOS',
        ipados: 'iPadOS',
        linux: 'Linux',
        macos: 'macOS',
        windows: 'Windows',
        unknown: 'Unknown',
    }
    const platformLabel = platform ? platformLabels[platform] : undefined

    if (browser && platformLabel) {
        return { deviceLabel: `${browser} on ${platformLabel}`, platform }
    }
    if (browser) {
        return { deviceLabel: browser, platform }
    }
    return platform ? { platform } : {}
}

async function getAuthorizedServerSubscriptions(
    subscriptions: PushBlobSubscription[],
    scope: 'read' | 'write' = 'read',
): Promise<Array<{ blobId: string; accessToken: string }>> {
    const serverSubscriptions: Array<{ blobId: string; accessToken: string }> = []
    for (const sub of subscriptions) {
        try {
            const { token } = await sub.authProvider.getAccessToken(scope)
            serverSubscriptions.push({ blobId: sub.blobId, accessToken: token })
        } catch {
            // Skip blobs where token acquisition fails
        }
    }

    return serverSubscriptions
}

export async function computePushSubscriptionId(endpoint: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(endpoint))
    return encodeBase64Url(new Uint8Array(digest))
}

/**
 * Subscribe to push notifications for the given workspaces (Spec §6.5).
 * Each subscription carries a per-blob workspace access token obtained
 * via the auth provider's challenge-signing flow.
 */
export async function subscribeToPush(apiBaseUrl: string, subscriptions: PushBlobSubscription[]): Promise<boolean> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false
    if (!isPwaInstalled()) return false
    if (subscriptions.length === 0) return false

    // Request permission
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return false

    // Obtain access tokens for each blob via auth providers (Spec §6.5)
    const serverSubscriptions = await getAuthorizedServerSubscriptions(subscriptions)
    if (serverSubscriptions.length === 0) return false

    const vapidKey = await getVapidKey(apiBaseUrl)
    if (!vapidKey) return false

    const registration = await navigator.serviceWorker.ready
    let pushSubscription = await registration.pushManager.getSubscription()

    if (!pushSubscription) {
        pushSubscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey).buffer as ArrayBuffer,
        })
    }

    // Send subscription to server (upsert) — v2 format per Spec §6.5
    const p256dh = pushSubscription.getKey('p256dh')
    const auth = pushSubscription.getKey('auth')
    if (!p256dh || !auth) return false

    const pushApiBase = getPushApiBase(apiBaseUrl)
    const subscribeUrl = `${pushApiBase}/api/push/subscribe`
    const deviceMetadata = getNotificationDeviceMetadata()
    const res = await fetch(subscribeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            endpoint: pushSubscription.endpoint,
            keys: {
                p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dh))),
                auth: btoa(String.fromCharCode(...new Uint8Array(auth))),
            },
            subscriptions: serverSubscriptions,
            ...(deviceMetadata.deviceLabel ? { deviceLabel: deviceMetadata.deviceLabel } : {}),
            ...(deviceMetadata.platform ? { platform: deviceMetadata.platform } : {}),
        }),
    })
    recordBrowserHttpErrorResponse(res, { url: subscribeUrl, method: 'POST' })

    return res.ok
}

export async function unsubscribeFromPush(apiBaseUrl: string, subscriptions: PushBlobSubscription[] = []): Promise<boolean> {
    const subscription = await getCurrentBrowserPushSubscription()
    if (!subscription) return false

    const pushApiBase = getPushApiBase(apiBaseUrl)
    let body: { endpoint: string; subscriptions?: Array<{ blobId: string; accessToken: string }> } = {
        endpoint: subscription.endpoint,
    }
    if (subscriptions.length > 0) {
        const authorizedSubscriptions = await getAuthorizedServerSubscriptions(subscriptions)
        if (authorizedSubscriptions.length === 0) {
            throw new Error('Could not authorize push unsubscribe for any workspace.')
        }
        body = {
            endpoint: subscription.endpoint,
            subscriptions: authorizedSubscriptions,
        }
    }

    const subscribeUrl = `${pushApiBase}/api/push/subscribe`
    const res = await fetch(subscribeUrl, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    })
    recordBrowserHttpErrorResponse(res, { url: subscribeUrl, method: 'DELETE' })

    return res.ok
}

export async function unsubscribeBrowserPush(): Promise<void> {
    const subscription = await getCurrentBrowserPushSubscription()
    if (!subscription) return
    await subscription.unsubscribe()
}

/**
 * Startup reconciliation — sync push state with server (Spec §6.5).
 * Called after vault unlock when registry is available.
 */
export async function reconcilePushSubscriptions(apiBaseUrl: string, subscriptions: PushBlobSubscription[]): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    if (!isPwaInstalled()) return

    // If permission was revoked, clean up server-side
    if (Notification.permission === 'denied') {
        await unsubscribeFromPush(apiBaseUrl, subscriptions)
        return
    }

    const pushSubscription = await getCurrentBrowserPushSubscription()
    if (!pushSubscription || subscriptions.length === 0) return

    // Upsert with current subscriptions (v2 with access tokens)
    await subscribeToPush(apiBaseUrl, subscriptions)
}

export async function isPushSubscribed(): Promise<boolean> {
    return !!(await getCurrentBrowserPushSubscription())
}

export async function getCurrentPushSubscriptionId(): Promise<string | null> {
    const override = getTestModePushSubscriptionIdOverride()
    if (override) {
        return override
    }

    const subscription = await getCurrentBrowserPushSubscription()
    if (!subscription) return null
    return computePushSubscriptionId(subscription.endpoint)
}

export async function listNotificationDevices(
    apiBaseUrl: string,
    blobId: string,
    authProvider: PushAuthProvider,
): Promise<NotificationDeviceRecord[]> {
    const { token } = await authProvider.getAccessToken('write')
    const devicesUrl = `${getPushApiBase(apiBaseUrl)}/api/notifications/workspaces/${encodeURIComponent(blobId)}/devices`
    const res = await fetch(devicesUrl, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    })
    recordBrowserHttpErrorResponse(res, { url: devicesUrl, method: 'GET' })
    if (!res.ok) {
        throw new Error(`Failed to load notification devices (${res.status})`)
    }

    const data = await res.json() as { devices?: NotificationDeviceRecord[] }
    return data.devices ?? []
}

export async function revokeNotificationDevice(
    apiBaseUrl: string,
    blobId: string,
    subscriptionId: string,
    authProvider: PushAuthProvider,
): Promise<void> {
    const { token } = await authProvider.getAccessToken('write')
    const revokeUrl = `${getPushApiBase(apiBaseUrl)}/api/notifications/workspaces/${encodeURIComponent(blobId)}/devices/${encodeURIComponent(subscriptionId)}`
    const res = await fetch(revokeUrl, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    })
    recordBrowserHttpErrorResponse(res, { url: revokeUrl, method: 'DELETE' })
    if (!res.ok) {
        throw new Error(`Failed to revoke notification device (${res.status})`)
    }
}

// ── Notification activation state (spec §19.2) ─────────────────────

export type NotificationState =
    | 'off'
    | 'on'
    | 'requires-install'
    | 'permission-blocked'
    | 'not-supported'
    | 'server-unavailable'

export interface NotificationConfigDocument {
    v: '1'
    blobId: string
    notifyUrl: string
    notifyToken: string
    defaultAudience: string
}

/**
 * Check whether the server provides durable push subscription storage.
 * Per spec §19.2, callers map missing durability to `server-unavailable`
 * so the UI can keep the notification management entrypoint visible.
 */
export async function isPushDurable(apiBaseUrl: string): Promise<boolean> {
    try {
        const pushApiBase = getPushApiBase(apiBaseUrl)
        const statusUrl = `${pushApiBase}/api/push/status`
        const res = await fetch(statusUrl)
        recordBrowserHttpErrorResponse(res, { url: statusUrl, method: 'GET' })
        if (!res.ok) return false
        const data = await res.json() as { durable?: boolean }
        return data.durable === true
    } catch {
        return false
    }
}

/**
 * Compute the current notification activation state for a workspace (spec §19.2).
 * Takes into account browser support, PWA install, permission, server durability,
 * and actual push subscription state.
 */
export async function getNotificationState(apiBaseUrl: string, workspaceEnabled = false): Promise<NotificationState> {
    // Check browser support
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        return 'not-supported'
    }

    // Check PWA install
    if (!isPwaInstalled()) {
        return 'requires-install'
    }

    // Check permission
    if (Notification.permission === 'denied') {
        return 'permission-blocked'
    }

    // Keep the current-device state truthful for already-enabled workspaces even
    // if the separate durability probe is temporarily unavailable.
    if (workspaceEnabled && await isPushSubscribed()) {
        return 'on'
    }

    // Check server durability before offering activation for workspaces that are
    // not currently subscribed on this browser.
    const durable = await isPushDurable(apiBaseUrl)
    if (!durable) {
        return 'server-unavailable'
    }

    // Notifications are not active on this browser. The 'off' state signals the
    // UI to offer activation (subscribeToPush). This covers both:
    //  - workspaceEnabled=false: notifications not enabled for this workspace
    //  - workspaceEnabled=true but not subscribed on this browser: user can
    //    subscribe this device to receive push for the already-enabled workspace.
    return 'off'
}

export function parseNotificationConfig(
    text: string | null,
    expectedBlobId?: string,
): NotificationConfigDocument | null {
    if (!text) return null
    try {
        const parsed = JSON.parse(text) as Partial<NotificationConfigDocument>
        if (parsed.v !== '1') return null
        if (typeof parsed.blobId !== 'string' || parsed.blobId.length === 0) return null
        if (expectedBlobId && parsed.blobId !== expectedBlobId) return null
        if (typeof parsed.notifyUrl !== 'string' || parsed.notifyUrl.length === 0) return null
        if (typeof parsed.notifyToken !== 'string' || parsed.notifyToken.length === 0) return null
        if (typeof parsed.defaultAudience !== 'string' || parsed.defaultAudience.length === 0) return null
        return {
            v: '1',
            blobId: parsed.blobId,
            notifyUrl: parsed.notifyUrl,
            notifyToken: parsed.notifyToken,
            defaultAudience: parsed.defaultAudience,
        }
    } catch {
        return null
    }
}

export function formatNotificationConfig(config: NotificationConfigDocument): string {
    return `${JSON.stringify(config, null, 2)}\n`
}

export async function provisionNotificationConfig(
    apiBaseUrl: string,
    blobId: string,
    authProvider: PushAuthProvider,
    opts: { rotate?: boolean } = {},
): Promise<NotificationConfigDocument> {
    const { token } = await authProvider.getAccessToken('write')
    const notificationsApiBase = apiBaseUrl.replace(/\/api\/cryptoblobstore$/, '')
    const provisionUrl = `${notificationsApiBase}/api/notifications/workspaces/${encodeURIComponent(blobId)}/provision`
    const res = await fetch(provisionUrl, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rotate: opts.rotate === true }),
    })
    recordBrowserHttpErrorResponse(res, { url: provisionUrl, method: 'POST' })
    if (!res.ok) {
        throw new Error(`Notification config provisioning failed: ${res.status}`)
    }

    const payload = await res.json() as Partial<NotificationConfigDocument>
    if (
        payload.blobId !== blobId
        || typeof payload.notifyUrl !== 'string'
        || typeof payload.notifyToken !== 'string'
        || typeof payload.defaultAudience !== 'string'
    ) {
        throw new Error('Notification config provisioning returned an invalid payload.')
    }

    return {
        v: '1',
        blobId,
        notifyUrl: payload.notifyUrl,
        notifyToken: payload.notifyToken,
        defaultAudience: payload.defaultAudience,
    }
}
